import { useLocale as useLocaleR42 } from '@/lib/locale';
import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

/**
 * CoachChip — Phase 116a.
 *
 * The one way we show "who teaches this" across the product: public
 * website cards, parent portal class blocks + packages, Class Finder,
 * enrollment cards, the coach's own package list, open-schedule slots.
 *
 * `coach` is the backend's coach card:
 *   { staff_id, display_name, photo_url, is_owner, bio? }
 *
 * Sizes: 'xs' (inline pill), 'sm' (card footer), 'md' (detail header).
 * `label` prefixes the name ("Coach", "Coached by", "with"). Pass
 * label={null} to show just the name.
 */

export function coachInitials(name) {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return 'C';
    return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
}

const SIZES = {
    xs: { avatar: 'h-5 w-5', text: 'text-[11px]', gap: 'gap-1.5', fallback: 'text-[9px]' },
    sm: { avatar: 'h-7 w-7', text: 'text-xs', gap: 'gap-2', fallback: 'text-[10px]' },
    md: { avatar: 'h-10 w-10', text: 'text-sm', gap: 'gap-2.5', fallback: 'text-xs' },
    lg: { avatar: 'h-14 w-14', text: 'text-base', gap: 'gap-3', fallback: 'text-sm' },
};

export function CoachAvatar({ coach, size = 'sm', className = '', ringColor }) {
    const s = SIZES[size] || SIZES.sm;
    const name = coach?.display_name || 'Coach';
    return (
        <Avatar
            className={`${s.avatar} shrink-0 ring-2 ${className}`}
            style={{ '--tw-ring-color': ringColor || 'rgba(0, 212, 255, 0.45)' }}
            data-testid="coach-avatar"
        >
            {coach?.photo_url ? <AvatarImage src={coach.photo_url} alt={name} className="object-cover" /> : null}
            <AvatarFallback className={`bg-[#0a1628] font-semibold text-[#00d4ff] ${s.fallback}`}>
                {coachInitials(name)}
            </AvatarFallback>
        </Avatar>
    );
}

export function CoachChip({
    coach,
    size = 'sm',
    label = 'Coach',
    className = '',
    tone = 'light', // 'light' (on white cards) | 'dark' (on navy surfaces)
    showRole = false,
    testId = 'coach-chip',
}) {
    const { t: t42 } = useLocaleR42();
    if (!coach) return null;
    const s = SIZES[size] || SIZES.sm;
    const name = coach.display_name || 'Coach';
    const textColor = tone === 'dark' ? 'text-white' : 'text-[#0a1628]';
    const subColor = tone === 'dark' ? 'text-white/60' : 'text-[#1a3a5c]/60';
    return (
        <span
            className={`inline-flex items-center ${s.gap} ${className}`}
            title={`${label ? `${label} ` : ''}${name}`}
            data-testid={testId}
            data-coach-staff-id={coach.staff_id || 'owner'}
        >
            <CoachAvatar coach={coach} size={size} />
            <span className={`min-w-0 leading-tight ${s.text}`}>
                {label && <span className={`${subColor} mr-1`}>{label}</span>}
                <span className={`font-semibold ${textColor}`} data-testid={`${testId}-name`}>{name}</span>
                {showRole && (
                    <span className={`block text-[10px] ${subColor}`}>
                        {coach.is_owner ? t42("Owner · Head coach") : t42("Team coach")}
                    </span>
                )}
            </span>
        </span>
    );
}

export default CoachChip;
